
import { useFavorites } from '../../hooks/useFavorites';
import { usePriceMonitor } from '../../hooks/usePriceMonitor';
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FavoritesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const FavoritesSidebar = ({ isOpen, onClose }: FavoritesSidebarProps) => {
  const { favoriteItems, removeFromFavorites } = useFavorites();
  const { addToMonitor } = usePriceMonitor();
  const [addedItems, setAddedItems] = useState<Set<number>>(new Set());
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);

  const handleAddToMonitor = (product: any) => {
    const priceNumber = parseFloat(product.price.replace('€', '').replace('$', '').replace(',', ''));
    
    addToMonitor({
      id: product.id,
      name: product.name,
      currentPrice: priceNumber,
      image: product.image,
      targetPrice: priceNumber * 0.9,
      priceHistory: [
        { date: new Date().toISOString().split('T')[0], price: priceNumber }
      ]
    });

    setAddedItems(prev => new Set(prev).add(product.id));
    
    setTimeout(() => {
      setAddedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }, 3000);
  };

  const handleRemoveFromFavorites = (id: number) => {
    removeFromFavorites(id);
    setAddedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const handleShare = () => {
    const productIds = favoriteItems.map(item => item.id).join(',');
    const generatedLink = `${window.location.origin}/wishlist?items=${productIds}`;
    setShareLink(generatedLink);
    setShowShareModal(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy');
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Check out my wishlist on Elroy!');
    const body = encodeURIComponent(`Hey! I wanted to share my wishlist with you. Check out these great deals:\n\n${shareLink}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent('Check out my wishlist on Elroy! Great deals on tech products.');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareLink)}`, '_blank');
  };

  const shareViaFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`, '_blank');
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Check out my wishlist on Elroy! ${shareLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">My Favorites</h2>
            <div className="flex items-center gap-2">
              {favoriteItems.length > 0 && (
                <button 
                  onClick={handleShare}
                  className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
                  title="Share Wishlist"
                >
                  <i className="ri-share-line text-xl text-gray-600"></i>
                </button>
              )}
              <button 
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {favoriteItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-heart-line text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Your wishlist is empty
                </h3>
                <p className="text-gray-600 text-sm">
                  Start adding products to your favorites
                </p>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {/* Compare Button */}
                <Link
                  to="/compare"
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer"
                >
                  <i className="ri-scales-3-line"></i>
                  <span className="text-sm font-medium">Compare Products</span>
                </Link>

                {favoriteItems.map((item) => (
                  <div key={item.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex gap-3">
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {item.name}
                        </h4>
                        <p className="text-gray-600 text-sm mt-1">
                          {item.price}
                        </p>

                        {/* Actions */}
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => handleAddToMonitor(item)}
                            disabled={addedItems.has(item.id)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-xs ${
                              addedItems.has(item.id) 
                                ? 'bg-teal-100 text-teal-700' 
                                : 'bg-gray-800 text-white hover:bg-gray-700'
                            }`}
                          >
                            {addedItems.has(item.id) ? (
                              <>
                                <i className="ri-check-line mr-1"></i>
                                Tracking
                              </>
                            ) : (
                              'Track Price'
                            )}
                          </button>
                          <button 
                            onClick={() => handleRemoveFromFavorites(item.id)}
                            className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xs text-gray-600"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {favoriteItems.length > 0 && (
            <div className="p-6 border-t bg-gray-50">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">
                  {favoriteItems.length} items in your wishlist
                </p>
                <button 
                  onClick={handleShare}
                  className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-share-line"></i>
                  Share Wishlist
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 z-[60]"
            onClick={() => setShowShareModal(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl z-[60] p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Share Your Wishlist</h3>
              <button 
                onClick={() => setShowShareModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>

            {/* Share Link */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Share Link</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-600"
                />
                <button
                  onClick={copyToClipboard}
                  className={`px-4 py-2.5 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    copied ? 'bg-green-600 text-white' : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {copied ? (
                    <><i className="ri-check-line mr-1"></i>Copied!</>
                  ) : (
                    <><i className="ri-file-copy-line mr-1"></i>Copy</>
                  )}
                </button>
              </div>
            </div>

            {/* Social Share */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Share via</label>
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={shareViaEmail}
                  className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-gray-700"></i>
                  </div>
                  <span className="text-xs text-gray-600">Email</span>
                </button>
                <button
                  onClick={shareViaTwitter}
                  className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center">
                    <i className="ri-twitter-x-line text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">Twitter</span>
                </button>
                <button
                  onClick={shareViaFacebook}
                  className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-[#1877F2] rounded-full flex items-center justify-center">
                    <i className="ri-facebook-fill text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">Facebook</span>
                </button>
                <button
                  onClick={shareViaWhatsApp}
                  className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">WhatsApp</span>
                </button>
              </div>
            </div>

            {/* Items Preview */}
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-500 mb-3">Sharing {favoriteItems.length} items:</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {favoriteItems.slice(0, 5).map(item => (
                  <div key={item.id} className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                ))}
                {favoriteItems.length > 5 && (
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-gray-600">+{favoriteItems.length - 5}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FavoritesSidebar;
